import { useCallback, useMemo, useRef, useState } from "react";
import { importTeams } from "../../../api/index.js";
import ImportCSV from "./ImportCSV.js";
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Grid,
  Paper,
  Stack,
  Chip,
  Divider,
  Button,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";

/** ---------- Helpers UI ---------- */

const normalizeKeys = (row) => {
  const out = {};
  Object.keys(row).forEach((k) => {
    const nk = k.replace(/^\uFEFF/, "").trim();
    const v = row[k];
    out[nk] = typeof v === "string" ? v.trim() : v;
  });
  return out;
};

const ModeChip = ({ mode }) => {
  const color = mode === "created" ? "success" : mode === "updated" ? "info" : "default";
  return <Chip size="small" color={color} label={mode || "-"} />;
};

const mergeServerResult = (prev, next) => {
  if (!prev) return next;

  const p = prev.importSummary?.teams;
  const n = next.importSummary?.teams;

  // si pas de structure détaillée, fallback simple
  if (!p || !n) return next;

  const merged = {
    ...next,
    // on garde un message recalculé (voir plus bas)
    message: prev.message, // temporaire, on peut l’écraser ensuite
    importSummary: {
      ...next.importSummary,
      teams: {
        ...n,
        total: (p.total || 0) + (n.total || 0),
        successCount: (p.successCount || 0) + (n.successCount || 0),
        errorCount: (p.errorCount || 0) + (n.errorCount || 0),
        lookupStats: {
          competition: {
            found: (p.lookupStats?.competition?.found || 0) + (n.lookupStats?.competition?.found || 0),
            notFound: (p.lookupStats?.competition?.notFound || 0) + (n.lookupStats?.competition?.notFound || 0),
            byThirdParty:
              (p.lookupStats?.competition?.byThirdParty || 0) + (n.lookupStats?.competition?.byThirdParty || 0),
            byName: (p.lookupStats?.competition?.byName || 0) + (n.lookupStats?.competition?.byName || 0),
          },
        },
        lines: [...(p.lines || []), ...(n.lines || [])],
      },
    },
    details: {
      successes: [...(prev.details?.successes || []), ...(next.details?.successes || [])],
      errors: [...(prev.details?.errors || []), ...(next.details?.errors || [])],
    },
  };

  // message agrégé (plus fiable que le message serveur batché)
  const t = merged.importSummary.teams;
  merged.message = `Réception de ${t.total} lignes. Succès: ${t.successCount}. Erreurs: ${t.errorCount}.`;

  return merged;
};


const StatCard = ({ title, value, hint, tone = "default" }) => (
  <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color={tone === "danger" ? "error.main" : "text.secondary"}>
          {hint}
        </Typography>
      )}
    </Stack>
  </Paper>
);

const SectionTitle = ({ children }) => (
  <Typography variant="h6" sx={{ mt: 3, mb: 1.5 }}>
    {children}
  </Typography>
);

const LookupMiniStat = ({ title, stats }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px dashed #e0e0e0" }}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" variant="outlined" label="found" color="success" />
        <Typography variant="body2">{stats?.found ?? 0}</Typography>
        <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
        <Chip size="small" variant="outlined" label="notFound" color="error" />
        <Typography variant="body2">{stats?.notFound ?? 0}</Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" label="byThirdParty" variant="outlined" />
        <Typography variant="body2">{stats?.byThirdParty ?? 0}</Typography>
        <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
        <Chip size="small" label="byName" variant="outlined" />
        <Typography variant="body2">{stats?.byName ?? 0}</Typography>
      </Stack>
    </Stack>
  </Paper>
);

const TeamsCSV = () => {
  const [loading, setLoading] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const importStartedRef = useRef(false);

  const [clientErrors, setClientErrors] = useState([]);
  const [serverResult, setServerResult] = useState(null);

const csvTeamsMapping = {
  name: "team_name",
  fullName: "full_name",
  discipline: "discipline",
  provider: "Groupe",

  color: "home_color",
  awayColor: "away_color",
  isCountry: "is_country",

  stadiumName: "Stade",
  stadiumCapacity: "Capacité stade",
  country: "Pays",
  teamCreation: "Date de création",
  website: "Site web",
  president: "Président",
  coaches: "Entraîneurs",
  subscription: "Souscription",

  thirdPartyEntityId: "team_id",
  thirdPartyId: "thirdPartyTeam",

  competitionId: "competition_id",
  competitionThirdParty: "competition_third_party",
  competitionName: "competition_name",
  competitionSeason: "season",
};

  const col = (id, fallback, opts = {}) => ({
  id,
  label: csvTeamsMapping[id] ?? fallback ?? id,
  ...opts,
});

const columns = useMemo(
  () => [
    col("name"),
    col("discipline"),
    col("provider"),

    col("thirdPartyEntityId", null, { required: false }),
    col("thirdPartyId", null, { required: false }),

    col("fullName", null, { required: false }),
    col("color", null, { required: false }),
    col("awayColor", null, { required: false }),
    col("isCountry", null, { required: false }),
    col("country", null, { required: false }),
    col("stadiumName", null, { required: false }),
    col("stadiumCapacity", null, { required: false }),
    col("teamCreation", null, { required: false }),
    col("website", null, { required: false }),
    col("president", null, { required: false }),
    col("coaches", null, { required: false }),
    col("subscription", null, { required: false }),

    col("competitionId", null, { required: false }),
    col("competitionThirdParty", null, { required: false }),
    col("competitionSeason", null, { required: false }),
    col("competitionName", null, { required: false }),
  ],
  []
);

/**anciennes données
 *     () => [
      { id: "name", label: "Nom" },
      { id: "discipline", label: "Discipline" },
      { id: "provider", label: "Groupe" },
      { id: "competitionSeason", label: "Saison de la competition" },

      { id: "competitionName", label: "Competition", required: false },

      { id: "idTeam", label: "team_id", required: false },
      { id: "idCompetition", label: "competition_id", required: false },
      { id: "thirdParty", label: "thirdParty", required: false },

      { id: "fullName", label: "Nom complet", required: false },
      { id: "color", label: "Couleur domicile", required: false },
      { id: "awayColor", label: "Couleur extérieur", required: false },
      { id: "isCountry", label: "Equipe nationale", required: false },
      { id: "country", label: "Pays", required: false },
      { id: "stadiumName", label: "Stade", required: false },
      { id: "stadiumCapacity", label: "Capacité du stade", required: false },
      { id: "teamCreation", label: "Année de création", required: false },
      { id: "website", label: "Site web", required: false },
      { id: "president", label: "Président", required: false },
      { id: "coaches", label: "Entraineurs", required: false },
      { id: "subscription", label: "Souscription live", required: false },
    ],
    []
 */

  const requiredColumns = useMemo(
    () => columns.filter((c) => c.required !== false).map((c) => c.id),
    [columns]
  );

  const isNonEmpty = (val) => val !== null && val !== undefined && String(val).trim() !== "";

  const validateRow = (row) => {
    const missing = requiredColumns.filter((col) => !isNonEmpty(row[col]));

    // Compétition obligatoire : (thirdParty + idCompetition) OU competitionName
    const hasCompetKeys = isNonEmpty(row.competitionThirdParty) && isNonEmpty(row.competitionId);
    const hasCompetName = isNonEmpty(row.competitionName);
    if (!hasCompetKeys && !hasCompetName) {
      missing.push("competition (competitionId+competitionThirdParty OU competitionName)");
    }

    return { ok: missing.length === 0, missing };
  };

  const handleDownloadJson = (data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `import-teams-summary-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleTeamImport = useCallback(async (rows) => {
    const normalizedRows = rows.map(normalizeKeys);

    if (!importStartedRef.current) {
      importStartedRef.current = true;
      setLoading(true);
      setImportComplete(false);
      setClientErrors([]);
      setServerResult(null);
    }

    const nonEmptyRows = normalizedRows.filter((row) => Object.values(row).some(isNonEmpty));

    // Batch fantôme → ignore
    if (nonEmptyRows.length === 0) return;

    const invalids = [];
    const valids = [];

    nonEmptyRows.forEach((row, idx) => {
      const { ok, missing } = validateRow(row);
      if (!ok) invalids.push({ index: idx + 1, missing, row });
      else valids.push(row);
    });

    if (valids.length === 0) {
      const firstRow = nonEmptyRows[0] || normalizedRows[0];

      const missingStats = {};
      invalids.forEach((it) => it.missing.forEach((m) => (missingStats[m] = (missingStats[m] || 0) + 1)));

      setClientErrors(
        [
          `❌ Aucune ligne valide après validation.`,
          `Lignes reçues: ${normalizedRows.length}`,
          `Non vides: ${nonEmptyRows.length}`,
          `Valides: ${valids.length}`,
          `Invalides: ${invalids.length}`,
          `Colonnes manquantes (fréquence): ${
            Object.entries(missingStats)
              .sort((a, b) => b[1] - a[1])
              .map(([col, n]) => `${col} (${n})`)
              .join(", ") || "(aucune)"
          }`,
          firstRow ? `Clés détectées 1ère ligne: ${Object.keys(firstRow).join(", ")}` : null,
          ...invalids.slice(0, 20).map((it) => `Ligne ${it.index} → manquantes: ${it.missing.join(", ")}`),
          invalids.length > 20 ? `… ${invalids.length - 20} autre(s) ligne(s) invalide(s).` : null,
        ].filter(Boolean)
      );

      importStartedRef.current = false;
      setLoading(false);
      setImportComplete(true);
      return;
    }

    if (invalids.length > 0) {
        setClientErrors((prev) => [
          ...prev,
          `— Batch ${new Date().toLocaleTimeString()} —`,
          `⚠️ ${invalids.length} ligne(s) invalide(s) ignorée(s), import des ${valids.length} ligne(s) valides.`,
          ...invalids.slice(0, 20).map((it) => `Ligne ${it.index} → manquantes: ${it.missing.join(", ")}`),
        ]);
      }

    try {
      const { data } = await importTeams(valids);
      setServerResult((prev) => mergeServerResult(prev, data));
    } catch (error) {
      console.error(error);
      setClientErrors((prev) => [...prev, error?.message ?? "Erreur lors de l'import."]);
    }
  }, [requiredColumns]);

  const handleImportComplete = () => {
    importStartedRef.current = false;
    setImportComplete(true);
    setLoading(false);
  };

  const teamsSummary = serverResult?.importSummary?.teams;
  const lines = teamsSummary?.lines || [];
  const lookupStats = teamsSummary?.lookupStats || {};
  const detailsErrors = serverResult?.details?.errors || [];
  const hasServerErrors = (teamsSummary?.errorCount || 0) > 0 || detailsErrors.length > 0;

  const clientSeverity = clientErrors?.[0]?.startsWith("⚠️") ? "warning" : "error";

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 2 }}>
        Equipes
      </Typography>

      <ImportCSV columns={columns} process={handleTeamImport} onComplete={handleImportComplete} />

      {loading && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Traitement en cours…</Typography>
        </Stack>
      )}

      {!loading && importComplete && clientErrors.length > 0 && (
        <Alert severity={clientSeverity} sx={{ mt: 2 }}>
          <Stack spacing={1}>
            <strong>
              {clientSeverity === "warning"
                ? "Avertissements CSV (côté client)"
                : "Erreurs de validation CSV (côté client)"}
            </strong>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {clientErrors.map((e, i) => (
                <li key={i}>
                  <Typography variant="body2" component="span">
                    {e}
                  </Typography>
                </li>
              ))}
            </ul>
          </Stack>
        </Alert>
      )}

      {!loading && importComplete && serverResult && (
        <Box sx={{ mt: 2 }}>
          <Alert severity={hasServerErrors ? "warning" : "success"}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {serverResult.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Import (client): {new Date().toLocaleString()}
                </Typography>
              </Box>
              <Tooltip title="Exporter le résumé en JSON">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloudDownloadIcon />}
                  onClick={() => handleDownloadJson(serverResult)}
                >
                  Export JSON
                </Button>
              </Tooltip>
            </Stack>
          </Alert>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Total lignes" value={teamsSummary?.total ?? "-"} hint={teamsSummary?.name} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Succès" value={teamsSummary?.successCount ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Erreurs"
                value={teamsSummary?.errorCount ?? 0}
                tone="danger"
                hint={detailsErrors.length ? `${detailsErrors.length} détaillée(s)` : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Lignes détaillées" value={lines.length} hint="created/updated" />
            </Grid>
          </Grid>

          <SectionTitle>Résolution des entités (lookup)</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <LookupMiniStat title="competition" stats={lookupStats.competition} />
            </Grid>
          </Grid>

          <SectionTitle>Détail par ligne</SectionTitle>
          <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #eee" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#fafafa" }}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>Competition</TableCell>
                  <TableCell>competitionKey</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, i) => (
                <TableRow key={`${i}-${line.teamId ?? line.teamKey ?? "no-id"}`}>
                  <TableCell>{i + 1}</TableCell>
                    <TableCell><ModeChip mode={line.mode} /></TableCell>
                    <TableCell>
                      <Stack spacing={0}>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                          {line.teamKey || "-"}
                        </Typography>
                        {line.teamName && (
                          <Typography variant="caption" color="text.secondary">
                            Nom: {line.teamName}
                          </Typography>
                        )}
                        {line.teamId && (
                          <Typography variant="caption" color="text.secondary">
                            _id: {line.teamId}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        _id: {String(line.competitionId || "-")}
                      </Typography>
                      {line.competitionName && (
                        <Typography variant="caption" color="text.secondary">
                          Nom: {line.competitionName}
                        </Typography>
                      )}
                      <br/>
                      {line.season && (
                        <Typography variant="caption" color="text.secondary">
                          Saison: {line.season}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                        {line.competitionKey || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        Aucun détail de ligne renvoyé par le serveur.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {hasServerErrors && (
            <>
              <SectionTitle>Erreurs serveur</SectionTitle>
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">{detailsErrors.length} erreur(s) détaillée(s)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {detailsErrors.map((e, i) => (
                      <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        {/* <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Ligne {e.index}
                        </Typography> */}
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Erreur #{i + 1} {e.index ? `(ligne serveur ${e.index})` : ""}
                        </Typography>
                        <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                          {e.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                        >
                          {JSON.stringify(e.row, null, 2)}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TeamsCSV;
